<template>
    <div class="objects-page">
        <nav class="nav nav-tabs mb-3" aria-label="Раздел объектов">
            <router-link class="nav-link active" to="/objects">Рабочие объекты</router-link>
            <router-link class="nav-link" to="/objects/preparations">На подготовке</router-link>
        </nav>
        <div class="objects-toolbar">
            <b-form-input
                v-model.trim="filters.q"
                type="search"
                size="sm"
                placeholder="Поиск"
                class="objects-search"
                :class="{ 'objects-search--active': filters.q }"
                @input="onSearchInput"
            ></b-form-input>

            <v-select
                v-model="filters.issue"
                :options="issueFilterSelectOptions"
                :reduce="option => option.value"
                label="text"
                placeholder="Все отклонения"
                class="objects-select"
                :class="{ 'objects-select--active': filters.issue }"
                @input="applyFilters"
            ></v-select>

            <v-select
                v-model="filters.main_staff_id"
                :options="staffFilterOptions"
                :reduce="option => option.value"
                label="text"
                placeholder="Все прорабы"
                class="objects-select"
                :class="{ 'objects-select--active': filters.main_staff_id }"
                @input="applyFilters"
            ></v-select>

            <v-select
                v-model="filters.curator_id"
                :options="curatorFilterOptions"
                :reduce="option => option.value"
                label="text"
                placeholder="Все кураторы"
                class="objects-select"
                :class="{ 'objects-select--active': filters.curator_id }"
                @input="applyFilters"
            ></v-select>

            <v-select
                v-model="filters.has_access_user_id"
                :options="hasAccessFilterOptions"
                :reduce="option => option.value"
                label="text"
                placeholder="Все с доступом"
                class="objects-select"
                :class="{ 'objects-select--active': filters.has_access_user_id }"
                @input="applyFilters"
            ></v-select>
        </div>

        <div class="objects-status-panel" @wheel="scrollStatusPanelOnWheel">
            <button
                v-for="option in statusFilterOptions"
                :key="option.value === null ? 'actual' : option.value"
                type="button"
                class="objects-status-chip"
                :class="[
                    `objects-status-chip--${option.variant}`,
                    { 'objects-status-chip--active': statusChipActive(option.value) }
                ]"
                @click="setStatusFilter(option.value)"
            >
                <i
                    v-if="statusChipActive(option.value)"
                    class="fas fa-check objects-status-chip__check"
                    aria-hidden="true"
                ></i>
                {{ option.text }}
            </button>
        </div>

        <div v-if="activeFilterItems.length" class="objects-active-filters">
            <span class="objects-active-filters__label">
                <i class="fas fa-filter"></i>
                Включены фильтры:
            </span>
            <button
                v-for="item in activeFilterItems"
                :key="item.key"
                type="button"
                class="objects-active-filter"
                :class="`objects-active-filter--${item.variant}`"
                :title="`Снять фильтр: ${item.text}`"
                @click="clearFilterItem(item.key)"
            >
                <i class="fas fa-check" aria-hidden="true"></i>
                <span class="objects-active-filter__text">{{ item.text }}</span>
                <i class="fas fa-times objects-active-filter__remove" aria-hidden="true"></i>
            </button>
            <button type="button" class="objects-active-filters__reset" @click="resetFilters">
                Сбросить все
            </button>
        </div>

        <div class="objects-meta">
            <div class="text-muted">Найдено: {{ total }}</div>
            <div class="objects-actions">
                <b-button
                    v-if="canCreate"
                    size="sm"
                    variant="outline-primary"
                    @click="openObjectGroups"
                >
                    <i class="fas fa-layer-group mr-1"></i>
                    Группы объектов
                </b-button>
                <b-button
                    v-if="canManageAccess"
                    size="sm"
                    variant="outline-secondary"
                    :disabled="accessSettingsLoading"
                    @click="openAccessSettings"
                >
                    <i class="fas fa-user-shield mr-1"></i>
                    Настройки доступа
                </b-button>
                <b-button
                    v-if="canCreate"
                    size="sm"
                    variant="success"
                    @click="openCreate"
                >
                    <i class="fas fa-plus mr-1"></i>
                    Мастер создания объекта
                </b-button>
            </div>
        </div>

        <b-alert
            v-if="errorMessage"
            variant="danger"
            show
            dismissible
            class="mb-2"
            @dismissed="errorMessage = null"
        >
            {{ errorMessage }}
        </b-alert>

        <div class="objects-list-controls">
            <b-pagination
                v-model="currentPage"
                :total-rows="total"
                :per-page="limit"
                size="sm"
                class="mb-0"
                @input="onPageChange"
            ></b-pagination>
            <b-form-select
                v-model="limit"
                :options="pageOptions"
                size="sm"
                class="objects-limit"
                @change="onLimitChange"
            ></b-form-select>
        </div>

        <div class="objects-mobile-sort">
            <b-form-select
                v-model="tableSortBy"
                :options="sortOptions"
                size="sm"
                @change="onMobileSortChange"
            ></b-form-select>
            <b-button
                size="sm"
                variant="outline-secondary"
                @click="toggleMobileSortDirection"
            >
                <i class="fas" :class="tableSortDesc ? 'fa-sort-amount-down' : 'fa-sort-amount-up'"></i>
                {{ tableSortDesc ? 'Убыв.' : 'Возр.' }}
            </b-button>
        </div>

        <b-table
            class="objects-table"
            :items="items"
            :fields="fields"
            :busy="loading"
            responsive="lg"
            striped
            small
            hover
            sticky-header="75vh"
            :sort-by.sync="tableSortBy"
            :sort-desc.sync="tableSortDesc"
            :tbody-tr-class="tableRowClass"
            no-local-sorting
            show-empty
            empty-text="Объекты не найдены"
            @sort-changed="onSortChanged"
            @row-clicked="openObjectFromRow"
        >
            <template #table-busy>
                <div class="text-center text-muted py-4">Загрузка</div>
            </template>

            <template #cell(name)="data">
                <div class="objects-name">
                    <a
                        v-if="canOpenObjectCard(data.item)"
                        href=""
                        :data-object-id="data.item.building_id"
                        @click.prevent.stop="openObject(data.item.building_id, data.item)"
                    >
                        {{ data.item.name || `Объект #${data.item.building_id}` }}
                    </a>
                    <span
                        v-else
                        class="objects-name__text"
                        :data-object-id="data.item.building_id"
                    >
                        {{ data.item.name || `Объект #${data.item.building_id}` }}
                    </span>
                </div>
                <div class="text-muted small objects-name__meta">
                    #{{ data.item.building_id }} ·
                    <span v-if="data.item.object_group_id">
                        Группа:
                        <router-link v-if="canOpenObjectCard(data.item)" :to="`/objects/groups/${data.item.object_group_id}`" @click.native.stop>{{ data.item.object_group_name || `Группа #${data.item.object_group_id}` }} ↗</router-link>
                        <span v-else>{{ data.item.object_group_name || `Группа #${data.item.object_group_id}` }}</span>
                    </span>
                    <span v-else>{{ data.item.object_group_id === null ? 'Без группы' : 'Группа не загружена' }}</span>
                </div>
            </template>

            <template #cell(status)="data">
                <div class="objects-status-stack">
                    <b-badge :variant="statusVariant(data.item.status)">
                        {{ data.item.status_label || data.item.status }}
                    </b-badge>
                </div>
            </template>

            <template #cell(issues)="data">
                <div v-if="objectIssueLabels(data.item).length" class="objects-status-issues">
                    <span
                        v-for="label in objectIssueLabels(data.item)"
                        :key="label.key"
                        class="objects-issue-label"
                        :class="`objects-issue-label--${label.variant}`"
                        v-b-tooltip.hover.html
                        :title="label.title"
                    >
                        <i :class="label.icon" aria-hidden="true"></i>
                        {{ label.text }}
                    </span>
                </div>
                <span v-else class="text-muted">-</span>
            </template>

            <template #cell(last_report)="data">
                <div class="objects-last-report">
                    <b-badge
                        :variant="lastReportBadge(data.item).variant"
                        v-b-tooltip.hover
                        :title="lastReportBadge(data.item).title"
                    >
                        {{ lastReportBadge(data.item).text }}
                    </b-badge>
                    <small v-if="data.item.latest_report_date" class="text-muted">
                        {{ formatDateTime(data.item.latest_report_date) }}
                    </small>
                </div>
            </template>

            <template #cell(main_staff)="data">
                <EmployeeLink
                    v-if="data.item.main_staff_id"
                    :user="objectStaffUser(data.item)"
                    compact
                    name-order="last-first"
                    fallback="-"
                />
                <span v-else>-</span>
            </template>

            <template #cell(curator)="data">
                <EmployeeLink
                    v-if="data.item.curator_id"
                    :user="objectCuratorUser(data.item)"
                    compact
                    name-order="last-first"
                    fallback="-"
                />
                <span v-else>-</span>
            </template>

            <template #cell(contract)="data">
                <div>{{ data.item.contract_name || '-' }}</div>
                <small v-if="data.item.contract_date" class="text-muted">
                    {{ formatDate(data.item.contract_date) }}
                </small>
            </template>

            <template #cell(common_chat_id)="data">
                <ObjectTelegramLinks :object="data.item" />
            </template>

            <template #cell(flags)="data">
                <div class="objects-flags">
                    <i
                        v-if="hasHasAccess(data.item)"
                        class="fas fa-users text-primary"
                        v-b-tooltip.hover.html
                        :title="hasAccessTooltip(data.item)"
                    ></i>
                    <i
                        v-if="hasGalleryAccess(data.item)"
                        class="fas fa-images text-info"
                        v-b-tooltip.hover
                        title="Настроен доступ к галерее"
                    ></i>
                    <i
                        v-if="data.item.need_notification"
                        class="fas fa-bell text-success"
                        v-b-tooltip.hover
                        title="Включены напоминания"
                    ></i>
                </div>
            </template>

            <template #cell(actions)="data">
                <b-button
                    size="sm"
                    variant="link"
                    class="p-0"
                    :disabled="!canOpenObjectCard(data.item)"
                    :title="canOpenObjectCard(data.item) ? `Открыть карточку` : `Нет доступа к карточке`"
                    @click.stop="openObject(data.item.building_id, data.item)"
                >
                    <i class="fas fa-external-link-alt"></i>
                </b-button>
            </template>
        </b-table>

        <div class="objects-mobile-list">
            <div v-if="loading" class="objects-mobile-state">
                <b-spinner small class="mr-2"></b-spinner>
                Загрузка
            </div>
            <div v-else-if="!items.length" class="objects-mobile-state">
                Объекты не найдены
            </div>
            <template v-else>
                <b-card
                    v-for="(item, index) in items"
                    :key="item.building_id"
                    no-body
                    class="objects-mobile-card"
                    :data-object-id="item.building_id"
                    :class="[
                        { 'objects-highlight-target': isHighlightedObject(item) },
                        { 'objects-mobile-card--alt': index % 2 === 1 },
                        { 'objects-mobile-card--list-only': !canOpenObjectCard(item) }
                    ]"
                    @click="openObject(item.building_id, item)"
                >
                    <span
                        class="objects-mobile-card__accent"
                        :class="`bg-${statusVariant(item.status)}`"
                        aria-hidden="true"
                    ></span>
                    <div class="objects-mobile-card__header">
                        <div class="objects-mobile-card__title-block">
                            <a
                                v-if="canOpenObjectCard(item)"
                                href=""
                                class="objects-mobile-card__title"
                                @click.prevent.stop="openObject(item.building_id, item)"
                            >
                                {{ item.name || `Объект #${item.building_id}` }}
                            </a>
                            <span v-else class="objects-mobile-card__title">
                                {{ item.name || `Объект #${item.building_id}` }}
                            </span>
                            <div class="objects-mobile-card__meta">
                                <span>#{{ item.building_id }}</span>
                                <span v-if="item.object_group_id" class="objects-name__meta">
                                    Группа:
                                    <router-link v-if="canOpenObjectCard(item)" :to="`/objects/groups/${item.object_group_id}`" @click.native.stop>{{ item.object_group_name || `Группа #${item.object_group_id}` }} ↗</router-link>
                                    <span v-else>{{ item.object_group_name || `Группа #${item.object_group_id}` }}</span>
                                </span>
                                <span v-else>{{ item.object_group_id === null ? 'Без группы' : 'Группа не загружена' }}</span>
                            </div>
                        </div>
                        <div class="objects-mobile-card__status-stack">
                            <b-badge :variant="statusVariant(item.status)" class="objects-mobile-card__status">
                                {{ item.status_label || item.status }}
                            </b-badge>
                            <b-badge
                                :variant="lastReportBadge(item).variant"
                                class="objects-mobile-card__last-report"
                                v-b-tooltip.hover
                                :title="lastReportBadge(item).title"
                            >
                                <i class="fas fa-clipboard-list mr-1" aria-hidden="true"></i>
                                {{ lastReportBadge(item).text }}
                            </b-badge>
                        </div>
                    </div>

                    <div v-if="objectIssueLabels(item).length" class="objects-mobile-card__signals">
                        <span
                            v-for="label in objectIssueLabels(item)"
                            :key="label.key"
                            class="objects-issue-label"
                            :class="`objects-issue-label--${label.variant}`"
                            v-b-tooltip.hover.html
                            :title="label.title"
                        >
                            <i :class="label.icon" aria-hidden="true"></i>
                            {{ label.text }}
                        </span>
                    </div>

                    <div v-if="item.address" class="objects-mobile-card__address">
                        <i class="fas fa-map-marker-alt" aria-hidden="true"></i>
                        <span>{{ item.address }}</span>
                    </div>

                    <div class="objects-mobile-card__body">
                        <div class="objects-mobile-card__fact">
                            <span>Прораб</span>
                            <EmployeeLink
                                v-if="item.main_staff_id"
                                :user="objectStaffUser(item)"
                                compact
                                name-order="last-first"
                                fallback="-"
                            />
                            <strong v-else>-</strong>
                        </div>
                        <div class="objects-mobile-card__fact">
                            <span>Куратор</span>
                            <EmployeeLink
                                v-if="item.curator_id"
                                :user="objectCuratorUser(item)"
                                compact
                                name-order="last-first"
                                fallback="-"
                            />
                            <strong v-else>-</strong>
                        </div>
                        <div class="objects-mobile-card__fact">
                            <span>Договор</span>
                            <strong>{{ item.contract_name || '-' }}</strong>
                            <small v-if="item.contract_date">{{ formatDate(item.contract_date) }}</small>
                        </div>
                        <div class="objects-mobile-card__fact">
                            <span>Последний отчёт</span>
                            <strong>{{ lastReportBadge(item).text }}</strong>
                            <small v-if="item.latest_report_date">{{ formatDateTime(item.latest_report_date) }}</small>
                        </div>
                        <div class="objects-mobile-card__fact">
                            <span>Время отчёта</span>
                            <strong>{{ item.report_time || '-' }}</strong>
                        </div>
                    </div>

                    <div class="objects-mobile-card__footer">
                        <div class="objects-mobile-card__flags">
                            <span
                                v-if="hasHasAccess(item)"
                                class="objects-mobile-card__flag"
                                v-b-tooltip.hover.html
                                :title="hasAccessTooltip(item)"
                            >
                                <i class="fas fa-users text-primary" aria-hidden="true"></i>
                                {{ item.has_access.length }}
                            </span>
                            <span
                                v-if="hasGalleryAccess(item)"
                                class="objects-mobile-card__flag"
                                v-b-tooltip.hover
                                title="Настроен доступ к галерее"
                            >
                                <i class="fas fa-images text-info" aria-hidden="true"></i>
                            </span>
                            <span
                                v-if="item.need_notification"
                                class="objects-mobile-card__flag"
                                v-b-tooltip.hover
                                title="Включены напоминания"
                            >
                                <i class="fas fa-bell text-success" aria-hidden="true"></i>
                            </span>
                            <ObjectTelegramLinks :object="item" aria-label="Telegram" />
                        </div>
                        <b-button
                            size="sm"
                            variant="outline-primary"
                            :disabled="!canOpenObjectCard(item)"
                            :title="canOpenObjectCard(item) ? `Открыть карточку` : `Нет доступа к карточке`"
                            @click.stop="openObject(item.building_id, item)"
                        >
                            <i class="fas fa-eye mr-1"></i>
                            Подробнее
                        </b-button>
                    </div>
                </b-card>
            </template>
        </div>

        <b-modal
            v-model="accessSettingsVisible"
            title="Доступ к строительным объектам"
            size="xl"
            ok-title="Сохранить"
            cancel-title="Отмена"
            :ok-disabled="accessSettingsLoading || objectAccessSaving"
            @ok="saveAccessSettings"
        >
            <b-alert v-if="accessSettingsError" show variant="danger">
                {{ accessSettingsError }}
            </b-alert>

            <b-form-checkbox v-model="accessConfig.enabled" switch class="mb-3">
                Доступ к разделу включен
            </b-form-checkbox>

            <b-tabs content-class="pt-3">
                <b-tab title="Глобально" active>
                    <div class="objects-access-section">
                        <div class="objects-access-section__header">
                            <div>
                                <div class="objects-access-section__title">Список объектов</div>
                            </div>
                        </div>
                        <div class="objects-access-rows">
                            <div
                                v-for="right in listAccessRights"
                                :key="right.key"
                                class="objects-access-row"
                            >
                                <div class="objects-access-row__main">
                                    <div class="objects-access-row__title">{{ right.title }}</div>
                                    <div class="objects-access-row__summary">
                                        {{ globalAccessSummary(right.key) }}
                                    </div>
                                </div>
                                <div class="objects-access-row__controls">
                                    <b-form-checkbox
                                        v-if="right.key === `list_archive`"
                                        v-model="accessArchiveSameAsActive"
                                        switch
                                        size="sm"
                                        class="objects-access-row__switch"
                                        @change="onArchiveSameAsActiveChange"
                                    >
                                        Как действующие
                                    </b-form-checkbox>
                                    <b-button
                                        size="sm"
                                        :variant="expandedGlobalAccessRight === right.key ? `primary` : `outline-primary`"
                                        :disabled="right.key === `list_archive` && accessArchiveSameAsActive"
                                        @click="toggleGlobalAccessEditor(right.key)"
                                    >
                                        <i
                                            :class="expandedGlobalAccessRight === right.key ? `fas fa-chevron-up` : `fas fa-edit`"
                                            class="mr-1"
                                        ></i>
                                        {{ expandedGlobalAccessRight === right.key ? `Свернуть` : `Изменить` }}
                                    </b-button>
                                </div>
                                <div
                                    v-if="expandedGlobalAccessRight === right.key"
                                    class="objects-access-row__editor"
                                >
                                    <multiple-participants-config
                                        :value="globalAccessConfigForRight(right.key)"
                                        :users="participantUsers"
                                        :departments="participantDepartments"
                                        participant-label="участника"
                                        type-label="Тип доступа"
                                        all-text="Доступ получают все пользователи системы"
                                        :show-object-roles="true"
                                        :allowed-types="unifiedAccessTypes"
                                        :object-role-options="objectRoleOptions"
                                        object-role-hint-text="Роль проверяется на объекте: прораб, куратор или имеющий доступ."
                                        :show-fallback-option="false"
                                        :show-priority="false"
                                        :empty-text="`Правило ${right.title.toLowerCase()} не настроено`"
                                        hint-text="Добавьте пользователей, отделы или роли объекта"
                                        @input="onGlobalAccessConfigInput(right.key, $event)"
                                    />
                                </div>
                            </div>
                        </div>
                    </div>

                    <div class="objects-access-section">
                        <div class="objects-access-section__header">
                            <div>
                                <div class="objects-access-section__title">Карточки объектов</div>
                            </div>
                        </div>
                        <div class="objects-access-rows">
                            <div
                                v-for="right in cardAccessRights"
                                :key="right.key"
                                class="objects-access-row"
                            >
                                <div class="objects-access-row__main">
                                    <div class="objects-access-row__title">{{ right.title }}</div>
                                    <div class="objects-access-row__summary">
                                        {{ globalAccessSummary(right.key) }}
                                    </div>
                                </div>
                                <div class="objects-access-row__controls">
                                    <b-button
                                        size="sm"
                                        :variant="expandedGlobalAccessRight === right.key ? `primary` : `outline-primary`"
                                        @click="toggleGlobalAccessEditor(right.key)"
                                    >
                                        <i
                                            :class="expandedGlobalAccessRight === right.key ? `fas fa-chevron-up` : `fas fa-edit`"
                                            class="mr-1"
                                        ></i>
                                        {{ expandedGlobalAccessRight === right.key ? `Свернуть` : `Изменить` }}
                                    </b-button>
                                </div>
                                <div
                                    v-if="expandedGlobalAccessRight === right.key"
                                    class="objects-access-row__editor"
                                >
                                    <multiple-participants-config
                                        :value="globalAccessConfigForRight(right.key)"
                                        :users="participantUsers"
                                        :departments="participantDepartments"
                                        participant-label="участника"
                                        type-label="Тип доступа"
                                        all-text="Доступ получают все пользователи системы"
                                        :show-object-roles="true"
                                        :allowed-types="unifiedAccessTypes"
                                        :object-role-options="objectRoleOptions"
                                        object-role-hint-text="Роль проверяется на объекте: прораб, куратор или имеющий доступ."
                                        :show-fallback-option="false"
                                        :show-priority="false"
                                        :empty-text="`Правило ${right.title.toLowerCase()} не настроено`"
                                        hint-text="Добавьте пользователей, отделы или роли объекта"
                                        @input="onGlobalAccessConfigInput(right.key, $event)"
                                    />
                                </div>
                            </div>
                        </div>
                    </div>
                </b-tab>

                <b-tab title="Выбранные объекты">
                    <b-row>
                        <b-col cols="12" md="5" class="mb-3 mb-md-0">
                            <b-form-input
                                v-model="objectAccessSearch"
                                size="sm"
                                type="search"
                                placeholder="Поиск объекта"
                                autocomplete="off"
                            />
                            <div v-if="objectAccessLoading" class="text-center py-3">
                                <b-spinner small variant="secondary"></b-spinner>
                            </div>
                            <div v-else class="objects-access-list mt-2">
                                <b-button
                                    v-for="object in filteredObjectAccessObjects"
                                    :key="object.building_id"
                                    type="button"
                                    block
                                    size="sm"
                                    :variant="selectedObjectAccessId === object.building_id ? 'info' : 'outline-secondary'"
                                    class="text-left mb-2 objects-access-list__button"
                                    @click="selectObjectAccess(object)"
                                >
                                    <div class="d-flex justify-content-between align-items-start">
                                        <span class="font-weight-bold objects-access-list__name">
                                            {{ object.name }}
                                        </span>
                                        <i
                                            v-if="hasObjectAccessConfig(object)"
                                            class="fas fa-user-check ml-2"
                                            title="Доступ настроен"
                                        ></i>
                                    </div>
                                    <small class="d-block">
                                        #{{ object.building_id }} · {{ object.status_label }}
                                    </small>
                                </b-button>
                                <div
                                    v-if="!filteredObjectAccessObjects.length"
                                    class="text-muted text-center py-3 border rounded"
                                >
                                    Объекты не найдены
                                </div>
                            </div>
                        </b-col>
                        <b-col cols="12" md="7">
                            <div v-if="selectedObjectAccessObject">
                                <div class="objects-access-object-heading">
                                    {{ selectedObjectAccessObject.name }}
                                </div>
                                <div class="objects-access-section objects-access-section--selected">
                                    <div
                                        v-for="right in selectedObjectRightsMeta"
                                        :key="right.key"
                                        class="objects-access-row"
                                    >
                                        <div class="objects-access-row__main">
                                            <div class="objects-access-row__title">{{ right.title }}</div>
                                            <div class="objects-access-row__summary">
                                                {{ selectedObjectAccessSummary(right.key) }}
                                            </div>
                                        </div>
                                        <div class="objects-access-row__controls">
                                            <b-button
                                                size="sm"
                                                :variant="expandedSelectedObjectAccessRight === right.key ? `primary` : `outline-primary`"
                                                @click="toggleSelectedObjectAccessEditor(right.key)"
                                            >
                                                <i
                                                    :class="expandedSelectedObjectAccessRight === right.key ? `fas fa-chevron-up` : `fas fa-edit`"
                                                    class="mr-1"
                                                ></i>
                                                {{ expandedSelectedObjectAccessRight === right.key ? `Свернуть` : `Изменить` }}
                                            </b-button>
                                        </div>
                                        <div
                                            v-if="expandedSelectedObjectAccessRight === right.key"
                                            class="objects-access-row__editor"
                                        >
                                            <multiple-participants-config
                                                :value="selectedObjectAccessConfigForRight(right.key)"
                                                :users="participantUsers"
                                                :departments="participantDepartments"
                                                participant-label="участника"
                                                type-label="Тип доступа"
                                                :show-object-roles="true"
                                                :allowed-types="selectedObjectAccessTypes"
                                                :object-role-options="objectRoleOptions"
                                                object-role-hint-text="Роль проверяется на выбранном объекте."
                                                :show-fallback-option="false"
                                                :show-priority="false"
                                                :empty-text="right.key === `manage` ? `Для этого объекта управление не настроено` : `Для этого объекта ${right.title.toLowerCase()} не настроен`"
                                                hint-text="Добавьте пользователей, отделы или роли объекта"
                                                @input="onSelectedObjectAccessConfigInput(right.key, $event)"
                                            />
                                        </div>
                                    </div>
                                </div>
                                <div class="d-flex justify-content-end mt-2">
                                    <b-button
                                        size="sm"
                                        variant="success"
                                        :disabled="objectAccessSaving || !selectedObjectAccessDirty"
                                        @click="saveSelectedObjectAccess"
                                    >
                                        Сохранить доступ объекта
                                    </b-button>
                                </div>
                            </div>
                            <div v-else class="text-muted text-center py-3 border rounded">
                                Выберите объект слева
                            </div>
                        </b-col>
                    </b-row>
                </b-tab>
            </b-tabs>
        </b-modal>
    </div>
</template>

<script>
import MultipleParticipantsConfig from '@/views/Requests/RequestTypes/MultipleParticipantsConfig/MultipleParticipantsConfig.vue';
import EmployeeLink from '@/components/EmployeeLink.vue';
import ObjectTelegramLinks from './components/ObjectTelegramLinks.vue';

const SESSION_KEY = `objectCardsListReturn`;
const SESSION_TTL_MS = 20 * 60 * 1000;
const HIGHLIGHT_CLEANUP_DELAY_MS = 4500;
const HIGHLIGHT_BLINK_DURATION_MS = 1800;
const MANAGE_RIGHT_KEYS = [`manage`];
const SELECTED_MANAGE_RIGHT_KEYS = [`manage`];

function defaultFilters() {
    return {
        q: ``,
        status: null,
        issue: null,
        curator_id: null,
        main_staff_id: null,
        has_access_user_id: null
    };
}

function participantConfig() {
    return {participants: []};
}

function defaultRightConfig(roles = []) {
    return {
        all_objects: participantConfig(),
        own_objects: roles.length
            ? {participants: [{type: `object_role`, roles}]}
            : participantConfig()
    };
}

function defaultAccessConfig() {
    return {
        enabled: true,
        list_active: defaultRightConfig(),
        list_archive: defaultRightConfig(),
        read: defaultRightConfig([`main_staff`, `curator`, `has_access`]),
        manage: defaultRightConfig(),
        create_telegram_groups: defaultRightConfig()
    };
}

function defaultObjectAccessConfig() {
    return {
        read: participantConfig(),
        manage: participantConfig(),
        create_telegram_groups: participantConfig()
    };
}

function cloneJson(value) {
    return JSON.parse(JSON.stringify(value || {}));
}

export default {
    name: `Objects`,
    components: {
        ObjectTelegramLinks,
        MultipleParticipantsConfig,
        EmployeeLink
    },
    data() {
        return {
            filters: defaultFilters(),
            currentPage: 1,
            limit: 50,
            tableSortBy: `name`,
            tableSortDesc: false,
            pageOptions: [
                {value: 25, text: `25`},
                {value: 50, text: `50`},
                {value: 100, text: `100`},
                {value: 200, text: `200`}
            ],
            fields: [
                {key: `name`, label: `Объект`, sortable: true, class: `objects-name-col`},
                {key: `status`, label: `Статус`, sortable: true, class: `objects-status-col`},
                {key: `issues`, label: `Отклонения`, sortable: false, class: `objects-issues-col`},
                {key: `last_report`, label: `Последний отчёт`, sortable: true, class: `objects-last-report-col`},
                {key: `main_staff`, label: `Прораб`, sortable: true, class: `objects-person-col`},
                {key: `curator`, label: `Куратор`, sortable: true, class: `objects-person-col`},
                {key: `contract`, label: `Договор`, sortable: false, class: `objects-contract-col`},
                {key: `report_time`, label: `Время отчета`, sortable: true, class: `objects-report-time-col`},
                {key: `common_chat_id`, label: `Telegram`, sortable: false, class: `objects-chat-col`},
                {key: `flags`, label: ``, class: `objects-flags-col`},
                {key: `actions`, label: ``, class: `objects-actions-col`}
            ],
            sortOptions: [
                {value: `name`, text: `Название`},
                {value: `status`, text: `Статус`},
                {value: `last_report`, text: `Последний отчёт`},
                {value: `main_staff`, text: `Прораб`},
                {value: `curator`, text: `Куратор`},
                {value: `report_time`, text: `Время отчета`}
            ],
            errorMessage: null,
            accessSettingsVisible: false,
            accessSettingsLoading: false,
            accessSettingsError: null,
            accessConfig: defaultAccessConfig(),
            accessListActiveParticipantsConfig: participantConfig(),
            accessListArchiveParticipantsConfig: participantConfig(),
            accessReadParticipantsConfig: participantConfig(),
            accessManageParticipantsConfig: participantConfig(),
            accessTelegramParticipantsConfig: participantConfig(),
            accessArchiveSameAsActive: true,
            expandedGlobalAccessRight: null,
            expandedSelectedObjectAccessRight: null,
            objectAccessObjects: [],
            objectAccessSearch: ``,
            objectAccessLoading: false,
            objectAccessSaving: false,
            selectedObjectAccessId: null,
            selectedObjectAccessConfig: defaultObjectAccessConfig(),
            selectedObjectManageConfig: participantConfig(),
            selectedObjectAccessDirty: false,
            unifiedAccessTypes: [`all`, `specific_users`, `department`, `object_role`],
            selectedObjectAccessTypes: [`specific_users`, `department`, `object_role`],
            rightsMeta: [
                {key: `create_telegram_groups`, group: `card`, title: `Создание Telegram-групп объекта`},
                {
                    key: `list_active`,
                    group: `list`,
                    title: `Действующие`
                },
                {
                    key: `list_archive`,
                    group: `list`,
                    title: `Архивные`
                },
                {
                    key: `read`,
                    group: `card`,
                    title: `Просмотр карточки`
                },
                {
                    key: `manage`,
                    group: `card`,
                    title: `Управление`
                }
            ],
            selectedObjectRightsMeta: [
                {key: `create_telegram_groups`, title: `Создание Telegram-групп`},
                {key: `read`, title: `Просмотр`},
                {key: `manage`, title: `Редактирование и удаление`}
            ],
            objectRoleOptions: [
                {value: `main_staff`, text: `Прораб`},
                {value: `curator`, text: `Куратор`},
                {value: `has_access`, text: `Имеющий доступ`}
            ],
            searchTimer: null,
            highlightCleanupTimer: null,
            highlightedObjectId: null
        };
    },
    computed: {
        state() {
            return this.$store.state.objectCards;
        },
        items() {
            return this.state.items;
        },
        total() {
            return this.state.total;
        },
        loading() {
            return this.state.loading;
        },
        filterOptions() {
            return this.state.filterOptions || {};
        },
        users() {
            return this.$store.state.users || [];
        },
        accessSettings() {
            return this.state.accessSettings || {};
        },
        canManageAccess() {
            return this.accessSettings.can_manage_access === true;
        },
        canCreate() {
            return this.accessSettings.can_create === true ||
                this.accessSettings.can_manage_access === true ||
                this.$can(`manage`, `all`);
        },
        listAccessRights() {
            return this.rightsMeta.filter(right => right.group === `list`);
        },
        cardAccessRights() {
            return this.rightsMeta.filter(right => right.group === `card`);
        },
        participantUsers() {
            return (this.$store.state.users || [])
                .filter(user => user && user.user_id)
                .map(user => ({
                    value: user.user_id,
                    text: user.full_name || `${user.last_name || ``} ${user.first_name || ``}`.trim()
                }));
        },
        participantDepartments() {
            return (this.$store.state.departments || [])
                .filter(department => department && department.id)
                .map(department => ({
                    value: department.id,
                    text: department.name
                }));
        },
        filteredObjectAccessObjects() {
            const query = String(this.objectAccessSearch || ``).trim().toLowerCase();
            if (!query) {
                return this.objectAccessObjects;
            }
            return this.objectAccessObjects.filter(object => {
                return String(object.name || ``).toLowerCase().includes(query) ||
                    String(object.building_id || ``).includes(query);
            });
        },
        selectedObjectAccessObject() {
            return this.objectAccessObjects.find(object => {
                return Number(object.building_id) === Number(this.selectedObjectAccessId);
            }) || null;
        },
        statusFilterOptions() {
            const counts = {};
            (this.filterOptions.statuses || []).forEach(option => {
                counts[option.value] = option.count;
            });
            return [
                {value: null, text: `Актуальные`, variant: `primary`},
                {value: `active`, text: this.optionText(`В работе`, counts.active), variant: `success`},
                {value: `without_process`, text: this.optionText(`Без процесса`, counts.without_process), variant: `warning`},
                {value: `archive`, text: this.optionText(`Архив`, counts.archive), variant: `secondary`},
                {value: `all`, text: `Все`, variant: `dark`}
            ];
        },
        issueFilterOptions() {
            const counts = {};
            (this.filterOptions.issues || []).forEach(option => {
                counts[option.value] = option.count;
            });
            return [
                {value: `any`, text: this.optionText(`Любые`, counts.any), variant: `danger`},
                {value: `work_overruns`, text: this.optionText(`Сверх договора`, counts.work_overruns), variant: `danger`},
                {value: `material_issues`, text: this.optionText(`Материалы`, counts.material_issues), variant: `danger`},
                {value: `overdue`, text: this.optionText(`Просрочка`, counts.overdue), variant: `warning`}
            ];
        },
        issueFilterSelectOptions() {
            return this.withEmptyOption(this.issueFilterOptions, `Все отклонения`);
        },
        staffFilterOptions() {
            return this.withEmptyOption(this.enrichUserOptions(this.filterOptions.staff), `Все прорабы`);
        },
        curatorFilterOptions() {
            return this.withEmptyOption(this.enrichUserOptions(this.filterOptions.curators), `Все кураторы`);
        },
        hasAccessFilterOptions() {
            return this.withEmptyOption(this.enrichUserOptions(this.filterOptions.has_access_users), `Все с доступом`);
        },
        activeFilterItems() {
            const result = [];
            if (this.filters.q) {
                result.push({key: `q`, text: `Поиск: ${this.filters.q}`, variant: `primary`});
            }
            this.addFilterItem(result, `status`, `Статус`, this.statusFilterOptions, this.filters.status, `secondary`);
            this.addFilterItem(result, `issue`, `Отклонения`, this.issueFilterOptions, this.filters.issue, `danger`);
            this.addFilterItem(result, `main_staff_id`, `Прораб`, this.staffFilterOptions, this.filters.main_staff_id, `success`);
            this.addFilterItem(result, `curator_id`, `Куратор`, this.curatorFilterOptions, this.filters.curator_id, `info`);
            this.addFilterItem(result, `has_access_user_id`, `Доступ`, this.hasAccessFilterOptions, this.filters.has_access_user_id, `dark`);
            return result;
        }
    },
    beforeDestroy() {
        if (this.searchTimer) {
            clearTimeout(this.searchTimer);
        }
        this.clearHighlightCleanupTimer();
        if (typeof window !== `undefined`) {
            window.removeEventListener(`pageshow`, this.handlePageShow);
        }
    },
    methods: {
        normalizeParticipantConfig(config) {
            const source = config || {};
            return {
                participants: Array.isArray(source.participants)
                    ? source.participants
                    : []
            };
        },
        participantSignature(participant) {
            if (!participant || !participant.type) {
                return ``;
            }
            if (participant.type === `specific_users`) {
                const userIds = Array.isArray(participant.user_ids) ? participant.user_ids.slice() : [];
                return `${participant.type}:${userIds.map(Number).filter(Boolean).sort((a, b) => a - b).join(`,`)}`;
            }
            if (participant.type === `department`) {
                return `${participant.type}:${Number(participant.department_id) || ``}`;
            }
            if (participant.type === `object_role`) {
                const roles = Array.isArray(participant.roles) ? participant.roles.slice().sort() : [];
                return `${participant.type}:${roles.join(`,`)}`;
            }
            return participant.type;
        },
        participantConfigSignature(config) {
            return this.normalizeParticipantConfig(config).participants
                .map(participant => this.participantSignature(participant))
                .filter(Boolean)
                .sort()
                .join(`|`);
        },
        participantConfigEquals(first, second) {
            return this.participantConfigSignature(first) === this.participantConfigSignature(second);
        },
        optionTextByValue(options, value, fallback) {
            const found = (options || []).find(option => String(option.value) === String(value));
            return found ? found.text : fallback;
        },
        compactLabels(labels, emptyText = `Не настроено`) {
            const filtered = (labels || []).filter(Boolean);
            if (!filtered.length) {
                return emptyText;
            }
            if (filtered.length <= 2) {
                return filtered.join(`; `);
            }
            return `${filtered.slice(0, 2).join(`; `)} +${filtered.length - 2}`;
        },
        accessParticipantLabel(participant) {
            if (!participant || !participant.type) {
                return null;
            }
            if (participant.type === `all`) {
                return `Все пользователи`;
            }
            if (participant.type === `specific_users`) {
                const names = (participant.user_ids || []).map(userId => {
                    return this.optionTextByValue(this.participantUsers, userId, `Пользователь #${userId}`);
                });
                return this.compactLabels(names, null);
            }
            if (participant.type === `department`) {
                return this.optionTextByValue(
                    this.participantDepartments,
                    participant.department_id,
                    `Отдел #${participant.department_id}`
                );
            }
            if (participant.type === `object_role`) {
                const roles = (participant.roles || []).map(role => {
                    return this.optionTextByValue(this.objectRoleOptions, role, role);
                });
                return this.compactLabels(roles, null);
            }
            return null;
        },
        participantConfigSummary(config) {
            const labels = this.normalizeParticipantConfig(config).participants
                .map(participant => this.accessParticipantLabel(participant));
            return this.compactLabels(labels);
        },
        globalAccessSummary(right) {
            if (right === `list_archive` && this.accessArchiveSameAsActive) {
                return `Как действующие: ${this.participantConfigSummary(this.accessListActiveParticipantsConfig)}`;
            }
            return this.participantConfigSummary(this.globalAccessConfigForRight(right));
        },
        selectedObjectAccessConfigForRight(right) {
            if (right === `manage`) {
                return this.selectedObjectManageConfig;
            }
            return this.selectedObjectAccessConfig[right] || participantConfig();
        },
        selectedObjectAccessSummary(right) {
            return this.participantConfigSummary(this.selectedObjectAccessConfigForRight(right));
        },
        toggleGlobalAccessEditor(right) {
            this.expandedGlobalAccessRight = this.expandedGlobalAccessRight === right ? null : right;
        },
        toggleSelectedObjectAccessEditor(right) {
            this.expandedSelectedObjectAccessRight = this.expandedSelectedObjectAccessRight === right ? null : right;
        },
        syncArchiveSameAsActiveFlag() {
            this.accessArchiveSameAsActive = this.participantConfigEquals(
                this.accessListArchiveParticipantsConfig,
                this.accessListActiveParticipantsConfig
            );
            if (this.accessArchiveSameAsActive) {
                this.accessListArchiveParticipantsConfig = cloneJson(this.accessListActiveParticipantsConfig);
            }
        },
        onArchiveSameAsActiveChange(value) {
            this.accessArchiveSameAsActive = Boolean(value);
            if (this.accessArchiveSameAsActive) {
                this.accessListArchiveParticipantsConfig = cloneJson(this.accessListActiveParticipantsConfig);
                if (this.expandedGlobalAccessRight === `list_archive`) {
                    this.expandedGlobalAccessRight = null;
                }
            }
        },
        mergeParticipantConfigs(configs) {
            const participants = [];
            const added = new Set();
            (configs || []).forEach(config => {
                this.normalizeParticipantConfig(config).participants.forEach(participant => {
                    const normalized = cloneJson(participant);
                    const signature = this.participantSignature(normalized);
                    if (!signature || added.has(signature)) {
                        return;
                    }
                    added.add(signature);
                    participants.push(normalized);
                });
            });
            return {participants};
        },
        normalizeRightConfig(config, fallback) {
            const source = config || {};
            const base = fallback || defaultRightConfig();
            return {
                all_objects: this.normalizeParticipantConfig(source.all_objects || base.all_objects),
                own_objects: this.normalizeParticipantConfig(source.own_objects || base.own_objects)
            };
        },
        normalizeAccessConfig(config) {
            const source = config || {};
            const fallback = defaultAccessConfig();
            return {
                create_telegram_groups: this.normalizeRightConfig(source.create_telegram_groups, fallback.create_telegram_groups),
                enabled: source.enabled !== false,
                list_active: this.normalizeRightConfig(source.list_active, fallback.list_active),
                list_archive: this.normalizeRightConfig(source.list_archive, fallback.list_archive),
                read: this.normalizeRightConfig(source.read, fallback.read),
                manage: this.normalizeRightConfig(source.manage, fallback.manage)
            };
        },
        mergeRightParticipants(config, rightKeys) {
            const source = this.normalizeAccessConfig(config);
            return this.mergeParticipantConfigs(
                rightKeys.reduce((configs, key) => {
                    if (source[key]) {
                        configs.push(source[key].all_objects, source[key].own_objects);
                    }
                    return configs;
                }, [])
            );
        },
        splitAccessParticipants(config) {
            const allObjects = [];
            const ownObjects = [];
            this.normalizeParticipantConfig(config).participants.forEach(participant => {
                const target = participant && participant.type === `object_role`
                    ? ownObjects
                    : allObjects;
                target.push(cloneJson(participant));
            });
            return {
                all_objects: {participants: allObjects},
                own_objects: {participants: ownObjects}
            };
        },
        syncGlobalAccessParticipants(config) {
            this.accessTelegramParticipantsConfig = this.mergeRightParticipants(config, [`create_telegram_groups`]);
            this.accessListActiveParticipantsConfig = this.mergeRightParticipants(config, [`list_active`]);
            this.accessListArchiveParticipantsConfig = this.mergeRightParticipants(config, [`list_archive`]);
            this.accessReadParticipantsConfig = this.mergeRightParticipants(config, [`read`]);
            this.accessManageParticipantsConfig = this.mergeRightParticipants(config, MANAGE_RIGHT_KEYS);
            this.syncArchiveSameAsActiveFlag();
        },
        globalAccessConfigForRight(right) {
            if (right === `create_telegram_groups`) return this.accessTelegramParticipantsConfig;
            if (right === `list_active`) {
                return this.accessListActiveParticipantsConfig;
            }
            if (right === `list_archive`) {
                return this.accessListArchiveParticipantsConfig;
            }
            if (right === `manage`) {
                return this.accessManageParticipantsConfig;
            }
            return this.accessReadParticipantsConfig;
        },
        onGlobalAccessConfigInput(right, config) {
            const normalized = this.normalizeParticipantConfig(config);
            if (right === `create_telegram_groups`) { this.accessTelegramParticipantsConfig = normalized; return; }
            if (right === `list_active`) {
                this.accessListActiveParticipantsConfig = normalized;
                if (this.accessArchiveSameAsActive) {
                    this.accessListArchiveParticipantsConfig = cloneJson(normalized);
                }
            } else if (right === `list_archive`) {
                this.accessListArchiveParticipantsConfig = normalized;
                this.accessArchiveSameAsActive = this.participantConfigEquals(
                    this.accessListArchiveParticipantsConfig,
                    this.accessListActiveParticipantsConfig
                );
            } else if (right === `manage`) {
                this.accessManageParticipantsConfig = normalized;
            } else {
                this.accessReadParticipantsConfig = normalized;
            }
        },
        accessSettingsPayload() {
            const config = this.normalizeAccessConfig(this.accessConfig);
            const listActiveConfig = this.splitAccessParticipants(this.accessListActiveParticipantsConfig);
            const listArchiveSource = this.accessArchiveSameAsActive
                ? this.accessListActiveParticipantsConfig
                : this.accessListArchiveParticipantsConfig;
            const listArchiveConfig = this.splitAccessParticipants(listArchiveSource);
            const readConfig = this.splitAccessParticipants(this.accessReadParticipantsConfig);
            const manageConfig = this.splitAccessParticipants(this.accessManageParticipantsConfig);
            return {
                ...config,
                create_telegram_groups: this.splitAccessParticipants(this.accessTelegramParticipantsConfig),
                list_active: cloneJson(listActiveConfig),
                list_archive: cloneJson(listArchiveConfig),
                read: cloneJson(readConfig),
                manage: cloneJson(manageConfig)
            };
        },
        normalizeObjectAccessConfig(config) {
            const source = config || {};
            return {
                create_telegram_groups: this.normalizeParticipantConfig(source.create_telegram_groups),
                read: this.normalizeParticipantConfig(source.read),
                manage: this.normalizeParticipantConfig(source.manage)
            };
        },
        syncSelectedObjectManageConfig(config) {
            this.selectedObjectManageConfig = this.mergeParticipantConfigs(
                SELECTED_MANAGE_RIGHT_KEYS.map(key => config && config[key])
            );
        },
        setSelectedObjectAccessConfig(config, dirty = false) {
            const normalizedConfig = this.normalizeObjectAccessConfig(config);
            this.selectedObjectAccessConfig = cloneJson(normalizedConfig);
            this.syncSelectedObjectManageConfig(normalizedConfig);
            this.selectedObjectAccessDirty = dirty;
        },
        selectedObjectAccessPayload() {
            const readConfig = this.normalizeParticipantConfig(this.selectedObjectAccessConfig.read);
            const manageConfig = this.normalizeParticipantConfig(this.selectedObjectManageConfig);
            return {
                create_telegram_groups: this.normalizeParticipantConfig(this.selectedObjectAccessConfig.create_telegram_groups),
                read: cloneJson(readConfig),
                manage: cloneJson(manageConfig)
            };
        },
        hasObjectAccessConfig(object) {
            const config = object && object.object_access_config ? object.object_access_config : null;
            return Boolean(config && [`read`, `manage`, `create_telegram_groups`].some(key => {
                return config[key] &&
                    Array.isArray(config[key].participants) &&
                    config[key].participants.length;
            }));
        },
        async loadObjectAccess() {
            this.objectAccessLoading = true;
            try {
                const data = await this.$store.dispatch(`objectCards/fetchObjectAccess`, {
                    query: this.objectAccessSearch || undefined
                });
                this.objectAccessObjects = data && Array.isArray(data.items)
                    ? data.items.map(object => ({
                        ...object,
                        object_access_config: this.normalizeObjectAccessConfig(object.object_access_config)
                    }))
                    : [];

                const selectedStillExists = this.objectAccessObjects.some(object => {
                    return Number(object.building_id) === Number(this.selectedObjectAccessId);
                });
                if (!selectedStillExists) {
                    await this.selectObjectAccess(this.objectAccessObjects[0] || null, {skipSave: true});
                } else if (!this.selectedObjectAccessDirty) {
                    const selected = this.selectedObjectAccessObject;
                    this.setSelectedObjectAccessConfig(selected.object_access_config);
                }
            } finally {
                this.objectAccessLoading = false;
            }
        },
        async selectObjectAccess(object, options = {}) {
            if (!object) {
                this.selectedObjectAccessId = null;
                this.setSelectedObjectAccessConfig(defaultObjectAccessConfig());
                this.selectedObjectAccessDirty = false;
                return;
            }
            if (this.selectedObjectAccessDirty && !options.skipSave) {
                await this.saveSelectedObjectAccess();
            }
            this.selectedObjectAccessId = object.building_id;
            this.expandedSelectedObjectAccessRight = null;
            this.setSelectedObjectAccessConfig(object.object_access_config);
        },
        onSelectedObjectAccessConfigInput(right, config) {
            if (right === `manage`) {
                this.selectedObjectManageConfig = this.normalizeParticipantConfig(config);
            } else {
                this.$set(this.selectedObjectAccessConfig, right, this.normalizeParticipantConfig(config));
            }
            this.selectedObjectAccessDirty = true;
        },
        async saveSelectedObjectAccess() {
            const selected = this.selectedObjectAccessObject;
            if (!selected) {
                return;
            }
            this.accessSettingsError = null;
            this.objectAccessSaving = true;
            try {
                const data = await this.$store.dispatch(`objectCards/updateObjectAccess`, {
                    id: selected.building_id,
                    accessConfig: this.selectedObjectAccessPayload()
                });
                const savedConfig = this.normalizeObjectAccessConfig(data.access_config);
                const index = this.objectAccessObjects.findIndex(object => {
                    return Number(object.building_id) === Number(selected.building_id);
                });
                if (index !== -1) {
                    this.$set(this.objectAccessObjects, index, {
                        ...this.objectAccessObjects[index],
                        object_access_config: savedConfig
                    });
                }
                this.setSelectedObjectAccessConfig(savedConfig);
            } catch (err) {
                this.accessSettingsError = this.errorText(err, `Не удалось сохранить доступ объекта`);
                throw err;
            } finally {
                this.objectAccessSaving = false;
            }
        },
        async openAccessSettings() {
            this.accessSettingsError = null;
            this.accessSettingsVisible = true;
            this.accessSettingsLoading = true;
            try {
                await Promise.all([
                    this.$store.dispatch(`getUsers`, {full: true}),
                    this.$store.dispatch(`getDepartments`)
                ]);
                const data = await this.$store.dispatch(`objectCards/fetchAccessSettings`);
                this.accessConfig = this.normalizeAccessConfig(data.access_config);
                this.syncGlobalAccessParticipants(this.accessConfig);
                this.expandedGlobalAccessRight = null;
                this.expandedSelectedObjectAccessRight = null;
                await this.loadObjectAccess();
            } catch (err) {
                this.accessSettingsError = this.errorText(err, `Не удалось загрузить настройки доступа`);
            } finally {
                this.accessSettingsLoading = false;
            }
        },
        async saveAccessSettings(event) {
            event.preventDefault();
            this.accessSettingsError = null;
            this.accessSettingsLoading = true;
            try {
                if (this.selectedObjectAccessDirty) {
                    await this.saveSelectedObjectAccess();
                }
                const data = await this.$store.dispatch(
                    `objectCards/updateAccessSettings`,
                    this.accessSettingsPayload()
                );
                this.accessConfig = this.normalizeAccessConfig(data.access_config);
                this.syncGlobalAccessParticipants(this.accessConfig);
                this.accessSettingsVisible = false;
                await this.fetchList();
            } catch (err) {
                this.accessSettingsError = this.errorText(err, `Не удалось сохранить настройки доступа`);
            } finally {
                this.accessSettingsLoading = false;
            }
        },
        syncListControlsFromState() {
            const state = this.state || {};
            const limit = Number(state.limit) || this.limit || 50;
            const offset = Number(state.offset) || 0;
            this.limit = limit;
            this.currentPage = Math.max(1, Math.floor(offset / limit) + 1);
            this.tableSortBy = state.sortBy || this.tableSortBy;
            this.tableSortDesc = Boolean(state.sortDesc);
            this.filters = this.sanitizeFilters(state.filters);
            this.$store.commit(`objectCards/setFilters`, this.normalizedFilters());
        },
        applyListContext(context) {
            if (!context) {
                return;
            }
            const limit = Number(context.limit) || 50;
            const page = Math.max(1, Number(context.page) || 1);
            this.limit = limit;
            this.currentPage = page;
            this.tableSortBy = context.sortBy || `name`;
            this.tableSortDesc = Boolean(context.sortDesc);
            this.filters = this.sanitizeFilters(context.filters);
            this.$store.commit(`objectCards/setFilters`, this.normalizedFilters());
            this.$store.commit(`objectCards/setSort`, {
                sortBy: this.tableSortBy,
                sortDesc: this.tableSortDesc
            });
            this.$store.commit(`objectCards/setPagination`, {
                limit,
                offset: (page - 1) * limit
            });
        },
        saveReturnSession(objectId) {
            try {
                sessionStorage.setItem(SESSION_KEY, JSON.stringify({
                    objectId: Number(objectId),
                    page: this.currentPage,
                    limit: this.limit,
                    filters: this.normalizedFilters(),
                    sortBy: this.tableSortBy,
                    sortDesc: this.tableSortDesc,
                    savedAt: Date.now()
                }));
            } catch (e) { /* ignore */ }
        },
        readSession(objectId) {
            try {
                const raw = sessionStorage.getItem(SESSION_KEY);
                if (!raw) {
                    return null;
                }
                const data = JSON.parse(raw);
                if (
                    Number(data.objectId) === Number(objectId) &&
                    Date.now() - Number(data.savedAt || 0) < SESSION_TTL_MS
                ) {
                    return data;
                }
            } catch (e) { /* ignore */ }
            return null;
        },
        clearSession() {
            try {
                sessionStorage.removeItem(SESSION_KEY);
            } catch (e) { /* ignore */ }
        },
        clearHighlightCleanupTimer() {
            if (this.highlightCleanupTimer) {
                clearTimeout(this.highlightCleanupTimer);
                this.highlightCleanupTimer = null;
            }
        },
        scheduleHighlightCleanup(objectId) {
            this.clearHighlightCleanupTimer();
            this.highlightCleanupTimer = setTimeout(() => {
                if (this._isDestroyed) {
                    return;
                }
                this.highlightedObjectId = null;
                const currentHighlight = this.$route.query && this.$route.query.highlight;
                if (!currentHighlight || String(currentHighlight) !== String(objectId)) {
                    return;
                }
                const nextQuery = Object.assign({}, this.$route.query);
                delete nextQuery.highlight;
                this.$router.replace({
                    path: this.$route.path,
                    query: nextQuery
                }).catch(() => {});
            }, HIGHLIGHT_CLEANUP_DELAY_MS);
        },
        tableRowClass(item) {
            return this.isHighlightedObject(item) ? `objects-highlight-target` : ``;
        },
        isHighlightedObject(item) {
            return Boolean(item && this.highlightedObjectId && Number(item.building_id) === Number(this.highlightedObjectId));
        },
        hasActiveHighlight() {
            return Boolean((this.$route.query && this.$route.query.highlight) || this.highlightedObjectId);
        },
        isElementVisible(element) {
            if (!element) {
                return false;
            }
            const rect = element.getBoundingClientRect();
            return rect.width > 0 && rect.height > 0;
        },
        getTableScrollBody() {
            return this.$el ? this.$el.querySelector(`.objects-table .b-table-sticky-header`) : null;
        },
        resolveHighlightTarget(objectId) {
            if (!this.$el) {
                return null;
            }
            const id = Number(objectId);
            if (!Number.isInteger(id) || id <= 0) {
                return null;
            }
            const tableMarker = this.$el.querySelector(`.objects-table [data-object-id="${id}"]`);
            const tableRow = tableMarker ? (tableMarker.closest(`tr`) || tableMarker) : null;
            const mobileCard = this.$el.querySelector(`.objects-mobile-card[data-object-id="${id}"]`);
            if (this.isElementVisible(tableRow)) {
                return tableRow;
            }
            if (this.isElementVisible(mobileCard)) {
                return mobileCard;
            }
            if (tableRow) {
                return tableRow;
            }
            if (mobileCard) {
                return mobileCard;
            }
            return this.$el.querySelector(`.objects-table .objects-highlight-target`)
                || this.$el.querySelector(`.objects-mobile-card.objects-highlight-target`);
        },
        scrollElementInside(target, scrollBody) {
            if (!target || !scrollBody) {
                return;
            }
            const bodyRect = scrollBody.getBoundingClientRect();
            const targetRect = target.getBoundingClientRect();
            const nextTop = scrollBody.scrollTop
                + targetRect.top
                - bodyRect.top
                - ((scrollBody.clientHeight - targetRect.height) / 2);
            if (typeof scrollBody.scrollTo === `function`) {
                scrollBody.scrollTo({top: Math.max(0, nextTop), behavior: `smooth`});
            } else {
                scrollBody.scrollTop = Math.max(0, nextTop);
            }
        },
        applyHighlightBlink(target) {
            if (!target) {
                return;
            }
            const cls = `objects-highlight-blink`;
            const tagName = String(target.tagName || ``).toUpperCase();
            const elements = tagName === `TR`
                ? Array.prototype.slice.call(target.children || [])
                : [target];

            target.classList.remove(cls);
            elements.forEach(element => element.classList.remove(cls));
            void target.offsetWidth;
            target.classList.add(cls);
            elements.forEach(element => element.classList.add(cls));

            const setInlineHighlight = active => elements.forEach(element => {
                element.style.transition = `background-color 0.12s ease, box-shadow 0.12s ease`;
                if (!active) {
                    element.style.backgroundColor = ``;
                    element.style.boxShadow = ``;
                } else if (tagName === `TR`) {
                    element.style.backgroundColor = `#fff3cd`;
                    element.style.boxShadow = `inset 0 0 0 2px rgba(255, 193, 7, 0.82)`;
                } else {
                    element.style.boxShadow = `0 0 0 3px rgba(255, 193, 7, 0.9), 0 4px 14px rgba(255, 193, 7, 0.28)`;
                }
            });
            let inlineHighlightActive = true;
            setInlineHighlight(true);
            const inlineBlinkInterval = setInterval(() => {
                inlineHighlightActive = !inlineHighlightActive;
                setInlineHighlight(inlineHighlightActive);
            }, 180);

            setTimeout(() => {
                clearInterval(inlineBlinkInterval);
                target.classList.remove(cls);
                elements.forEach(element => {
                    element.classList.remove(cls);
                    element.style.transition = ``;
                    element.style.backgroundColor = ``;
                    element.style.boxShadow = ``;
                });
            }, HIGHLIGHT_BLINK_DURATION_MS);
        },
        async scrollToHighlight(objectId) {
            this.highlightedObjectId = Number(objectId);
            await this.$nextTick();
            await this.$nextTick();
            const target = this.resolveHighlightTarget(objectId);
            if (!target) {
                return;
            }
            const scrollBody = target.closest(`.b-table-sticky-header`);
            if (scrollBody && scrollBody.scrollHeight > scrollBody.clientHeight) {
                this.scrollElementInside(target, scrollBody);
            } else {
                target.scrollIntoView({behavior: `smooth`, block: `center`});
            }
            setTimeout(() => {
                if (this._isDestroyed) {
                    return;
                }
                const currentTarget = this.resolveHighlightTarget(objectId) || target;
                const currentScrollBody = currentTarget.closest(`.b-table-sticky-header`);
                if (currentScrollBody && currentScrollBody.scrollHeight > currentScrollBody.clientHeight) {
                    this.scrollElementInside(currentTarget, currentScrollBody);
                }
                this.applyHighlightBlink(currentTarget);
            }, 550);
            setTimeout(() => {
                if (!this._isDestroyed && this.hasActiveHighlight()) {
                    const currentTarget = this.resolveHighlightTarget(objectId);
                    const currentScrollBody = currentTarget && currentTarget.closest(`.b-table-sticky-header`);
                    if (currentTarget && currentScrollBody && currentScrollBody.scrollHeight > currentScrollBody.clientHeight) {
                        this.scrollElementInside(currentTarget, currentScrollBody);
                    }
                }
            }, 1100);
        },
        async restoreFromHighlight(highlightId) {
            const objectId = Number(highlightId);
            if (!Number.isInteger(objectId) || objectId <= 0) {
                await this.fetchList();
                return;
            }
            const session = this.readSession(objectId);
            if (session) {
                this.clearSession();
                this.applyListContext(session);
            }
            await this.fetchList({silent: Boolean(session && this.items.length)});
            await this.scrollToHighlight(objectId);
            this.scheduleHighlightCleanup(objectId);
        },
        handlePageShow() {
            const highlight = this.$route.query && this.$route.query.highlight;
            if (highlight) {
                this.scrollToHighlight(highlight);
            }
        },
        async fetchList(options = {}) {
            this.errorMessage = null;
            try {
                await this.$store.dispatch(`objectCards/fetchList`, options);
            } catch (err) {
                this.errorMessage = this.errorText(err, `Ошибка загрузки объектов`);
            }
        },
        async fetchInitialData() {
            this.syncListControlsFromState();
            try {
                await Promise.all([
                    this.$store.dispatch(`getUsers`, {full: true}),
                    this.$store.dispatch(`objectCards/fetchAccessSettings`)
                ]);
            } catch (err) {
                this.errorMessage = this.errorText(err, `Ошибка загрузки данных раздела`);
            }
            if (this.accessSettings.can_access_preparations && !this.accessSettings.can_list && !this.accessSettings.can_read && !this.accessSettings.can_create) {
                this.$router.replace('/objects/preparations'); return;
            }
            const highlight = this.$route.query && this.$route.query.highlight;
            if (highlight) {
                await this.restoreFromHighlight(highlight);
            } else {
                await this.fetchList();
            }
        },
        sanitizeFilters(filters) {
            const source = Object.assign(defaultFilters(), filters || {});
            return {
                q: source.q || ``,
                status: source.status || null,
                issue: source.issue || null,
                curator_id: source.curator_id || null,
                main_staff_id: source.main_staff_id || null,
                has_access_user_id: source.has_access_user_id || null
            };
        },
        normalizedFilters() {
            return Object.assign({}, this.filters, {
                q: this.filters.q || null,
                status: this.filters.status || null,
                issue: this.filters.issue || null,
                curator_id: this.filters.curator_id || null,
                main_staff_id: this.filters.main_staff_id || null,
                has_access_user_id: this.filters.has_access_user_id || null
            });
        },
        applyFilters() {
            this.currentPage = 1;
            this.errorMessage = null;
            this.$store.dispatch(`objectCards/applyFilters`, this.normalizedFilters())
                .catch(err => {
                    this.errorMessage = this.errorText(err, `Ошибка применения фильтра`);
                });
        },
        resetFilters() {
            this.filters = defaultFilters();
            this.currentPage = 1;
            this.$store.dispatch(`objectCards/applyFilters`, this.normalizedFilters())
                .catch(err => {
                    this.errorMessage = this.errorText(err, `Ошибка сброса фильтров`);
                });
        },
        clearFilterItem(key) {
            if (key === `q`) {
                this.filters.q = ``;
            } else if (Object.prototype.hasOwnProperty.call(this.filters, key)) {
                this.filters[key] = null;
            }
            this.applyFilters();
        },
        setStatusFilter(value) {
            this.filters.status = value || null;
            this.applyFilters();
        },
        statusChipActive(value) {
            return value === null
                ? !this.filters.status
                : String(this.filters.status || ``) === String(value);
        },
        scrollStatusPanelOnWheel(event) {
            const target = event && event.currentTarget;
            if (!target || event.ctrlKey || target.scrollWidth <= target.clientWidth) {
                return;
            }
            const delta = Math.abs(event.deltaX) > Math.abs(event.deltaY) ? event.deltaX : event.deltaY;
            if (!delta) {
                return;
            }
            const previousScrollLeft = target.scrollLeft;
            target.scrollLeft += delta;
            if (target.scrollLeft !== previousScrollLeft) event.preventDefault();
        },
        onSearchInput() {
            if (this.searchTimer) {
                clearTimeout(this.searchTimer);
            }
            this.searchTimer = setTimeout(() => this.applyFilters(), 300);
        },
        onPageChange(page) {
            this.$store.dispatch(`objectCards/setPage`, page)
                .catch(err => {
                    this.errorMessage = this.errorText(err, `Ошибка загрузки страницы`);
                });
        },
        onLimitChange(limit) {
            this.currentPage = 1;
            this.$store.dispatch(`objectCards/setLimit`, Number(limit))
                .catch(err => {
                    this.errorMessage = this.errorText(err, `Ошибка изменения размера страницы`);
                });
        },
        onSortChanged(ctx) {
            const sortBy = ctx && ctx.sortBy ? ctx.sortBy : `name`;
            const sortDesc = ctx ? Boolean(ctx.sortDesc) : false;
            this.tableSortBy = sortBy;
            this.tableSortDesc = sortDesc;
            this.currentPage = 1;
            this.$store.dispatch(`objectCards/setSort`, {sortBy, sortDesc})
                .catch(err => {
                    this.errorMessage = this.errorText(err, `Ошибка сортировки объектов`);
                });
        },
        onMobileSortChange() {
            this.onSortChanged({
                sortBy: this.tableSortBy,
                sortDesc: this.tableSortDesc
            });
        },
        toggleMobileSortDirection() {
            this.tableSortDesc = !this.tableSortDesc;
            this.onMobileSortChange();
        },
        async prepareHighlightNavigation(objectId) {
            this.saveReturnSession(objectId);
            const currentHighlight = this.$route.query && this.$route.query.highlight;
            if (String(currentHighlight || ``) === String(objectId)) {
                return;
            }
            const nextQuery = Object.assign({}, this.$route.query, {highlight: String(objectId)});
            await this.$router.replace({
                path: this.$route.path,
                query: nextQuery
            }).catch(() => {});
        },
        canOpenObjectCard(item) {
            return !item || item.can_open_card !== false;
        },
        async openObject(id, item = null) {
            if (!this.canOpenObjectCard(item)) {
                return;
            }
            const objectId = Number(id);
            if (!Number.isInteger(objectId) || objectId <= 0) {
                return;
            }
            await this.prepareHighlightNavigation(objectId);
            this.$router.push({
                name: `object-card`,
                params: {id: String(objectId)}
            }).catch(err => {
                if (!err || err.name !== `NavigationDuplicated`) {
                    throw err;
                }
            });
        },
        openObjectFromRow(item) {
            if (item && item.building_id) {
                this.openObject(item.building_id, item);
            }
        },
        openCreate() {
            this.$router.push({name: `CreateObjectWizard`});
        },
        openObjectGroups() {
            this.$router.push({name: `object-groups`});
        },
        addFilterItem(result, key, label, options, value, variant) {
            if (value === null || value === undefined || value === ``) {
                return;
            }
            const found = (options || []).find(option => String(option.value) === String(value));
            result.push({
                key,
                text: `${label}: ${found ? found.text : value}`,
                variant: variant || (found && found.variant) || `secondary`
            });
        },
        withEmptyOption(options, text) {
            return [{value: null, text}].concat(options || []);
        },
        enrichUserOptions(options) {
            return (options || []).map(option => ({
                value: option.value,
                text: this.optionText(this.userName(option.value) || option.text || `Пользователь #${option.value}`, option.count)
            }));
        },
        optionText(text, count) {
            return Number.isFinite(Number(count)) ? `${text} (${count})` : text;
        },
        formatDateTime(value) {
            if (!value) {
                return ``;
            }
            const date = new Date(value);
            if (!Number.isFinite(date.getTime())) {
                return ``;
            }
            return date.toLocaleString(`ru-RU`, {
                day: `2-digit`,
                month: `2-digit`,
                year: `numeric`,
                hour: `2-digit`,
                minute: `2-digit`
            });
        },
        daysSince(value) {
            if (!value) {
                return null;
            }
            const date = new Date(value);
            if (!Number.isFinite(date.getTime())) {
                return null;
            }
            const today = new Date();
            const todayStart = new Date(today.getFullYear(), today.getMonth(), today.getDate()).getTime();
            const dateStart = new Date(date.getFullYear(), date.getMonth(), date.getDate()).getTime();
            return Math.max(0, Math.floor((todayStart - dateStart) / 86400000));
        },
        lastReportBadge(item) {
            const date = item && item.latest_report_date;
            const days = this.daysSince(date);
            if (days === null) {
                return {
                    text: `Нет отчётов`,
                    title: `По объекту ещё нет отчётов по сменам`,
                    variant: `secondary`
                };
            }
            const text = days === 0 ? `Сегодня` : `${days} ${this.pluralizeDays(days)}`;
            return {
                text,
                title: `Последний отчёт: ${this.formatDateTime(date)}`,
                variant: days <= 1 ? `success` : (days <= 3 ? `info` : (days <= 7 ? `warning` : `danger`))
            };
        },
        pluralizeDays(value) {
            const number = Math.abs(Number(value) || 0);
            const mod10 = number % 10;
            const mod100 = number % 100;
            if (mod10 === 1 && mod100 !== 11) {
                return `день`;
            }
            if (mod10 >= 2 && mod10 <= 4 && (mod100 < 12 || mod100 > 14)) {
                return `дня`;
            }
            return `дней`;
        },
        countryText(value) {
            const map = {BY: `Беларусь`, RU: `Россия`};
            return map[value] || value || `-`;
        },
        userName(userId) {
            if (!userId) {
                return ``;
            }
            const user = this.users.find(item => Number(item.user_id) === Number(userId));
            return user ? (user.full_name || [user.last_name, user.first_name].filter(Boolean).join(` `).trim()) : ``;
        },
        userById(userId) {
            if (!userId) {
                return null;
            }
            return this.users.find(item => Number(item.user_id) === Number(userId)) || null;
        },
        objectStaffUser(item) {
            return this.objectRowUser(item, {
                id: item && item.main_staff_id,
                fullName: item && item.staff_full_name,
                phone: item && item.staff_phone
            });
        },
        objectCuratorUser(item) {
            return this.objectRowUser(item, {
                id: item && item.curator_id,
                fullName: item && item.curator_full_name,
                phone: item && item.curator_phone
            });
        },
        objectRowUser(item, fallback) {
            const existing = this.userById(fallback && fallback.id);
            if (existing) {
                return existing;
            }
            const names = String((fallback && fallback.fullName) || '')
                .trim()
                .split(/\s+/)
                .filter(Boolean);
            return {
                user_id: fallback && fallback.id,
                last_name: names[0] || '',
                first_name: names.slice(1).join(' '),
                full_name: (fallback && fallback.fullName) || this.userName(fallback && fallback.id),
                phone: fallback && fallback.phone
            };
        },
        statusVariant(status) {
            const map = {
                active: `success`,
                archive: `secondary`,
                without_process: `warning`
            };
            return map[status] || `light`;
        },
        hasHasAccess(item) {
            return Array.isArray(item.has_access) && item.has_access.length > 0;
        },
        hasGalleryAccess(item) {
            const config = item && item.gallery_access_config;
            return Boolean(config && Array.isArray(config.participants) && config.participants.length);
        },
        objectIssueLabels(item) {
            const summary = item && item.issue_summary ? item.issue_summary : {};
            const labels = [];
            const workOverruns = Number(summary.work_overruns_count) || 0;
            const materialIssues = Number(summary.material_issues_count) || 0;
            const overdueRequests = Number(summary.overdue_requests_count) || 0;
            const overdueTasks = Number(summary.overdue_tasks_count) || 0;
            if (workOverruns > 0) {
                labels.push({
                    key: `work-overruns`,
                    variant: `danger`,
                    icon: `fas fa-chart-line`,
                    text: `Сверх договора ${workOverruns}`,
                    title: `Выполнено больше чем по договору: ${workOverruns}`
                });
            }
            if (materialIssues > 0) {
                labels.push({
                    key: `material-issues`,
                    variant: `danger`,
                    icon: `fas fa-exclamation-triangle`,
                    text: `Материалы ${materialIssues}`,
                    title: `Отклонения материалов: ${materialIssues}`
                });
            }
            const overdueTotal = overdueRequests + overdueTasks;
            if (overdueTotal > 0) {
                labels.push({
                    key: `overdue`,
                    variant: `warning`,
                    icon: `fas fa-clock`,
                    text: `Просрочка ${overdueTotal}`,
                    title: [
                        `Просрочено`,
                        `Заявки: ${overdueRequests}`,
                        `Задачи: ${overdueTasks}`
                    ].join(`<br>`)
                });
            }
            return labels;
        },
        hasAccessTooltip(item) {
            const names = (item.has_access || [])
                .map(this.userName)
                .filter(Boolean);
            if (!names.length) {
                return `Имеющие доступ: ${item.has_access.length}`;
            }
            return `<div class="text-left"><strong>Имеющие доступ</strong><br>${names.join(`<br>`)}</div>`;
        },
        formatDate(value) {
            if (!value) {
                return ``;
            }
            const date = new Date(value);
            if (!Number.isFinite(date.getTime())) {
                return value;
            }
            return date.toLocaleDateString(`ru-RU`, {
                day: `2-digit`,
                month: `2-digit`,
                year: `numeric`
            });
        },
        errorText(err, fallback) {
            if (err && err.response && err.response.data) {
                return err.response.data.msg || err.response.data;
            }
            return fallback;
        }
    },
    mounted() {
        if (typeof window !== `undefined`) {
            window.addEventListener(`pageshow`, this.handlePageShow);
        }
        this.fetchInitialData();
    }
};
</script>

<style scoped>
.objects-page {
    padding: 8px;
}

.objects-toolbar {
    display: grid;
    grid-template-columns: minmax(220px, 1.2fr) repeat(4, minmax(180px, 0.8fr));
    gap: 8px;
    align-items: center;
    margin-bottom: 8px;
}

.objects-search,
.objects-select {
    font-size: 0.875rem;
}

.objects-search {
    min-height: 34px;
    border-color: #b6c2cf;
    border-radius: 999px;
    box-shadow: 0 1px 2px rgba(15, 23, 42, .08);
    font-weight: 700;
}

.objects-search:focus {
    border-color: #0d6efd;
    box-shadow: 0 0 0 3px rgba(13, 110, 253, .18);
}

.objects-search--active {
    border-color: #ffc107;
    box-shadow: 0 0 0 3px rgba(255, 193, 7, .24);
}

.objects-select--active >>> .vs__dropdown-toggle {
    border-color: #ffc107;
    box-shadow: 0 0 0 2px rgba(255, 193, 7, .24);
}

.objects-select--active >>> .vs__selected {
    color: #5c4300;
    font-weight: 700;
}

.objects-status-panel {
    display: flex;
    gap: 8px;
    overflow-x: auto;
    padding: 2px 0 8px;
    -webkit-overflow-scrolling: touch;
    scrollbar-width: thin;
}

.objects-status-chip {
    flex: 0 0 auto;
    min-height: 31px;
    padding: .25rem .55rem;
    border: 1px solid transparent;
    border-radius: 999px;
    font-size: 0.84rem;
    line-height: 1.2;
    white-space: nowrap;
    color: #495057;
    transition: box-shadow .15s ease, transform .15s ease, filter .15s ease;
}

.objects-status-chip:hover {
    filter: brightness(.96);
}

.objects-status-chip--active {
    color: #fff;
    font-weight: 600;
    box-shadow: 0 0 0 2px rgba(0, 0, 0, .18);
    transform: translateY(-1px);
}

.objects-status-chip--primary {
    background: #e7f1ff;
    border-color: #9ec5fe;
    color: #084298;
}

.objects-status-chip--success {
    background: #d1e7dd;
    border-color: #a3cfbb;
    color: #0f5132;
}

.objects-status-chip--warning {
    background: #fff3cd;
    border-color: #ffda6a;
    color: #664d03;
}

.objects-status-chip--secondary {
    background: #f1f3f5;
    border-color: #ced4da;
    color: #495057;
}

.objects-status-chip--dark {
    background: #e9ecef;
    border-color: #adb5bd;
    color: #212529;
}

.objects-status-chip--primary.objects-status-chip--active {
    background: #007bff;
    border-color: #007bff;
}

.objects-status-chip--success.objects-status-chip--active {
    background: #28a745;
    border-color: #28a745;
}

.objects-status-chip--warning.objects-status-chip--active {
    background: #ffc107;
    border-color: #ffc107;
    color: #212529;
}

.objects-status-chip--secondary.objects-status-chip--active {
    background: #6c757d;
    border-color: #6c757d;
}

.objects-status-chip--dark.objects-status-chip--active {
    background: #343a40;
    border-color: #343a40;
}

.objects-status-chip__check {
    margin-right: 5px;
}

.objects-active-filters {
    display: flex;
    align-items: center;
    flex-wrap: wrap;
    gap: .35rem;
    margin: 0 0 8px;
    padding: .4rem .5rem;
    border: 1px solid #ffe08a;
    border-radius: .35rem;
    background: #fff8e1;
    color: #5c4300;
    font-size: .84rem;
}

.objects-active-filters__label {
    display: inline-flex;
    align-items: center;
    gap: .3rem;
    flex: 0 0 auto;
    font-weight: 700;
}

.objects-active-filter {
    display: inline-flex;
    align-items: center;
    gap: .25rem;
    max-width: 100%;
    min-height: 28px;
    padding: .2rem .45rem;
    border: 1px solid transparent;
    border-radius: 999px;
    color: #fff;
    font-size: .8rem;
    font-weight: 700;
    line-height: 1.2;
    white-space: nowrap;
}

.objects-active-filter__text {
    min-width: 0;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
}

.objects-active-filter__remove {
    margin-left: .1rem;
    opacity: 0.75;
}

.objects-active-filters__reset {
    flex: 0 0 auto;
    min-height: 28px;
    padding: .2rem .5rem;
    border: 1px solid #b38300;
    border-radius: .25rem;
    background: #fff;
    color: #5c4300;
    font-size: .8rem;
    font-weight: 700;
    line-height: 1.2;
    white-space: nowrap;
}

.objects-active-filter--primary {
    background: #007bff;
    border-color: #007bff;
}

.objects-active-filter--success {
    background: #28a745;
    border-color: #28a745;
}

.objects-active-filter--warning {
    background: #ffc107;
    border-color: #ffc107;
    color: #212529;
}

.objects-active-filter--secondary {
    background: #6c757d;
    border-color: #6c757d;
}

.objects-active-filter--dark {
    background: #343a40;
    border-color: #343a40;
}

.objects-active-filter--info {
    background: #17a2b8;
    border-color: #17a2b8;
}

.objects-meta,
.objects-list-controls,
.objects-actions {
    display: flex;
    align-items: center;
}

.objects-actions {
    gap: 8px;
    flex-wrap: wrap;
    justify-content: flex-end;
}

.objects-access-section {
    overflow: hidden;
    border: 1px solid #d9e2ec;
    border-radius: 8px;
    background: #ffffff;
}

.objects-access-section + .objects-access-section {
    margin-top: 12px;
}

.objects-access-section__header {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 10px;
    padding: 10px 12px;
    border-bottom: 1px solid #d9e2ec;
    background: #f6f8fb;
}

.objects-access-section__title,
.objects-access-object-heading {
    color: #212529;
    font-weight: 800;
}

.objects-access-object-heading {
    margin-bottom: 8px;
}

.objects-access-row {
    display: grid;
    grid-template-columns: minmax(0, 1fr) auto;
    gap: 10px;
    align-items: center;
    padding: 10px 12px;
    border-bottom: 1px solid #edf2f7;
}

.objects-access-row:last-child {
    border-bottom: 0;
}

.objects-access-row__main {
    min-width: 0;
}

.objects-access-row__title {
    color: #1f2937;
    font-weight: 700;
    line-height: 1.2;
}

.objects-access-row__summary {
    overflow: hidden;
    margin-top: 3px;
    color: #6c757d;
    font-size: 0.86rem;
    line-height: 1.3;
    text-overflow: ellipsis;
}

.objects-access-row__controls {
    display: flex;
    align-items: center;
    justify-content: flex-end;
    flex-wrap: wrap;
    gap: 8px;
}

.objects-access-row__switch {
    margin: 0;
    white-space: nowrap;
}

.objects-access-row__editor {
    grid-column: 1 / -1;
    min-width: 0;
    padding-top: 10px;
    border-top: 1px dashed #cbd5e1;
}

.objects-access-list {
    max-height: 58vh;
    overflow-y: auto;
    padding-right: 4px;
}

.objects-access-list__button {
    white-space: normal;
}

.objects-access-list__name {
    min-width: 0;
    overflow: hidden;
    text-overflow: ellipsis;
}

.objects-meta {
    justify-content: space-between;
    gap: 8px;
    margin-bottom: 8px;
}

.objects-list-controls {
    justify-content: flex-end;
    gap: 8px;
    margin-bottom: 8px;
}

.objects-limit {
    width: 86px;
}

.objects-mobile-sort {
    display: none;
    gap: 8px;
    margin-bottom: 8px;
}

.objects-mobile-list {
    display: none;
}

.objects-mobile-state {
    padding: 18px 10px;
    border: 1px dashed #cfd8e3;
    border-radius: 8px;
    background: #f8fafc;
    color: #6c757d;
    text-align: center;
}

.objects-mobile-card {
    position: relative;
    overflow: hidden;
    border: 1px solid #cbd5e1;
    border-radius: 8px;
    background: #ffffff;
    box-shadow: 0 3px 10px rgba(15, 23, 42, 0.13);
    cursor: pointer;
}

.objects-mobile-card--alt {
    background: #f9fbfd;
}

.objects-mobile-card--list-only {
    cursor: default;
}

.objects-mobile-card__accent {
    position: absolute;
    top: 0;
    bottom: 0;
    left: 0;
    width: 5px;
    z-index: 1;
}

.objects-mobile-card__header {
    display: flex;
    align-items: flex-start;
    justify-content: space-between;
    gap: 10px;
    padding: 10px 10px 9px 15px;
    border-bottom: 1px solid #dbe3ec;
    background: linear-gradient(180deg, #f8fbff 0%, #eef3f8 100%);
}

.objects-mobile-card__title-block {
    min-width: 0;
    flex: 1 1 auto;
}

.objects-mobile-card__title {
    display: block;
    overflow: hidden;
    color: #1f2937;
    font-weight: 700;
    line-height: 1.2;
    text-overflow: ellipsis;
}

.objects-mobile-card__meta {
    display: flex;
    align-items: center;
    flex-wrap: wrap;
    gap: 4px 8px;
    margin-top: 3px;
    color: #6c757d;
    font-size: 12px;
    line-height: 1.2;
}

.objects-mobile-card__meta span + span::before {
    content: "";
    display: inline-block;
    width: 4px;
    height: 4px;
    margin-right: 8px;
    border-radius: 50%;
    background: #adb5bd;
    vertical-align: middle;
}

.objects-mobile-card__status {
    flex: 0 0 auto;
    white-space: normal;
    text-align: right;
}

.objects-mobile-card__last-report {
    flex: 0 0 auto;
    max-width: 100%;
    white-space: normal;
    text-align: right;
}

.objects-mobile-card__status-stack {
    display: flex;
    align-items: flex-end;
    flex: 0 0 auto;
    flex-direction: column;
    gap: 5px;
    max-width: 48%;
}

.objects-mobile-card__issues {
    display: flex;
    justify-content: flex-end;
    flex-wrap: wrap;
    gap: 4px;
}

.objects-mobile-card__signals {
    display: flex;
    flex-wrap: wrap;
    gap: 4px;
    padding: 7px 10px 0 15px;
}

.objects-mobile-card__address {
    display: flex;
    gap: 7px;
    padding: 8px 10px 8px 15px;
    border-bottom: 1px solid #e8edf3;
    color: #495057;
    font-size: 13px;
    line-height: 1.3;
}

.objects-mobile-card__address i {
    flex: 0 0 auto;
    margin-top: 2px;
    color: #6c757d;
}

.objects-mobile-card__body {
    display: grid;
    grid-template-columns: repeat(2, minmax(0, 1fr));
    gap: 8px 10px;
    padding: 10px 10px 10px 15px;
    background: rgba(255, 255, 255, 0.64);
}

.objects-mobile-card__fact {
    min-width: 0;
    padding: 7px 8px;
    border: 1px solid #e1e8f0;
    border-radius: 6px;
    background: rgba(255, 255, 255, 0.72);
}

.objects-mobile-card__fact span {
    display: block;
    margin-bottom: 2px;
    color: #6c757d;
    font-size: 12px;
    line-height: 1.2;
}

.objects-mobile-card__fact strong,
.objects-mobile-card__fact a {
    display: block;
    overflow: hidden;
    color: #212529;
    font-size: 13px;
    font-weight: 700;
    line-height: 1.2;
    text-overflow: ellipsis;
    white-space: nowrap;
}

.objects-mobile-card__fact small {
    display: block;
    margin-top: 2px;
    color: #6c757d;
    font-size: 11px;
}

.objects-mobile-card__footer {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 8px;
    padding: 8px 10px 9px 15px;
    border-top: 1px solid #dbe3ec;
    background: #f6f8fb;
}

.objects-mobile-card__flags {
    display: flex;
    align-items: center;
    flex-wrap: wrap;
    gap: 6px;
    min-width: 0;
}

.objects-mobile-card__flag {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    gap: 4px;
    min-width: 28px;
    min-height: 26px;
    padding: 3px 7px;
    border: 1px solid #d5dde6;
    border-radius: 999px;
    background: #ffffff;
    color: #495057;
    font-size: 12px;
    font-weight: 700;
    line-height: 1;
}

.objects-mobile-card.objects-highlight-target {
    border-color: #ffc107;
    box-shadow: 0 0 0 2px rgba(255, 193, 7, 0.72), 0 5px 16px rgba(15, 23, 42, 0.18);
}

.objects-mobile-card.objects-highlight-blink {
    animation: objects-highlight-card-blink-anim 0.32s ease-in-out 5;
}

.objects-name {
    font-weight: 600;
}

.objects-name__meta {
    overflow-wrap: anywhere;
    max-width: 100%;
}

.objects-name__text {
    display: inline-block;
    color: #1f2937;
}

.objects-status-stack {
    display: flex;
    align-items: flex-start;
    flex-direction: column;
    gap: 5px;
}

.objects-status-issues {
    display: flex;
    flex-wrap: wrap;
    gap: 4px;
}

.objects-last-report {
    display: flex;
    align-items: flex-start;
    flex-direction: column;
    gap: 3px;
    min-width: 0;
}

.objects-flags {
    display: flex;
    align-items: center;
    justify-content: center;
    flex-wrap: wrap;
    gap: 8px;
    min-height: 20px;
}

.objects-issue-label {
    display: inline-flex;
    align-items: center;
    gap: 4px;
    min-height: 22px;
    padding: 2px 7px;
    border: 1px solid transparent;
    border-radius: 999px;
    font-size: 11px;
    font-weight: 800;
    line-height: 1.15;
    white-space: nowrap;
}

.objects-issue-label--danger {
    border-color: #f1aeb5;
    background: #f8d7da;
    color: #842029;
}

.objects-issue-label--warning {
    border-color: #ffda6a;
    background: #fff3cd;
    color: #664d03;
}

.objects-issue-label--info {
    border-color: #9eeaf9;
    background: #cff4fc;
    color: #055160;
}

.objects-table {
    cursor: pointer;
    width: 100%;
}

.objects-table,
.objects-table >>> table {
    table-layout: fixed;
}

.objects-table >>> table {
    width: 100%;
}

.objects-table >>> th,
.objects-table >>> td {
    padding-right: .35rem;
    padding-left: .35rem;
    overflow-wrap: anywhere;
    vertical-align: middle;
}

.objects-table >>> .objects-name-col {
    width: 18%;
}

.objects-table >>> .objects-status-col {
    width: 8%;
}

.objects-table >>> .objects-issues-col {
    width: 12%;
}

.objects-table >>> .objects-last-report-col {
    width: 10%;
}

.objects-table >>> .objects-person-col {
    width: 10%;
}

.objects-table >>> .objects-person-col .employee-link,
.objects-table >>> .objects-person-col .employee-link-wrap {
    min-width: 0;
    width: 100%;
}

.objects-table >>> .objects-person-col .employee-link {
    align-items: flex-start;
}

.objects-table >>> .objects-person-col .employee-link__name {
    overflow: visible;
    text-overflow: clip;
    white-space: normal;
    overflow-wrap: anywhere;
}

.objects-table >>> .objects-contract-col {
    width: 9%;
}

.objects-table >>> .objects-report-time-col {
    width: 7%;
}

.objects-table >>> .objects-chat-col {
    width: 8%;
}

.objects-table >>> .objects-flags-col {
    width: 4%;
}

.objects-table >>> .objects-actions-col {
    width: 4%;
    text-align: center;
}

.objects-table >>> tbody tr.objects-highlight-target > td {
    background: #fff3cd !important;
}

.objects-table >>> tbody tr.objects-highlight-blink > td,
.objects-table >>> tbody td.objects-highlight-blink {
    animation: objects-highlight-cell-blink-anim 0.32s ease-in-out 5;
}

@keyframes objects-highlight-cell-blink-anim {
    0% {
        background-color: #fff3cd;
        box-shadow: inset 0 0 0 2px rgba(255, 193, 7, 0.82);
    }

    50% {
        background-color: #ffffff;
        box-shadow: inset 0 0 0 0 rgba(255, 193, 7, 0);
    }

    100% {
        background-color: #fff3cd;
        box-shadow: inset 0 0 0 2px rgba(255, 193, 7, 0.82);
    }
}

@keyframes objects-highlight-card-blink-anim {
    0% {
        box-shadow: 0 0 0 2px rgba(255, 193, 7, 0.72), 0 5px 16px rgba(15, 23, 42, 0.18);
    }

    50% {
        box-shadow: 0 3px 10px rgba(15, 23, 42, 0.13);
    }

    100% {
        box-shadow: 0 0 0 2px rgba(255, 193, 7, 0.72), 0 5px 16px rgba(15, 23, 42, 0.18);
    }
}

@media (max-width: 992px) {
    .objects-toolbar {
        grid-template-columns: 1fr 1fr;
    }
}

@media (min-width: 993px) and (max-width: 1500px) {
    .objects-toolbar {
        grid-template-columns: repeat(3, minmax(0, 1fr));
    }
}

@media (max-width: 767.98px) {
    .objects-page {
        padding: 6px;
    }

    .objects-status-panel {
        margin-right: -6px;
        padding-right: 6px;
    }

    .objects-active-filters {
        align-items: flex-start;
        flex-wrap: nowrap;
        overflow-x: auto;
        -webkit-overflow-scrolling: touch;
    }

    .objects-active-filters__label {
        position: sticky;
        left: 0;
        z-index: 1;
        padding-right: 4px;
        background: #fff8e1;
    }

    .objects-table {
        display: none;
    }

    .objects-mobile-sort {
        display: grid;
        grid-template-columns: minmax(0, 1fr) auto;
        gap: 8px;
        margin: 8px 0;
    }

    .objects-mobile-list {
        display: flex;
        flex-direction: column;
        gap: 12px;
        padding-bottom: calc(12px + env(safe-area-inset-bottom, 0px));
    }
}

@media (max-width: 576px) {
    .objects-toolbar {
        grid-template-columns: 1fr;
    }

    .objects-search {
        border-radius: 8px;
    }

    .objects-meta,
    .objects-list-controls {
        align-items: stretch;
        flex-direction: column;
    }

    .objects-actions .btn,
    .objects-limit {
        width: 100%;
    }

    .objects-access-row {
        grid-template-columns: 1fr;
        align-items: stretch;
    }

    .objects-access-row__controls {
        justify-content: flex-start;
    }

    .objects-mobile-card__header {
        flex-direction: column;
        gap: 8px;
        padding: 10px 10px 9px 14px;
    }

    .objects-mobile-card__title {
        overflow: visible;
        white-space: normal;
        overflow-wrap: anywhere;
    }

    .objects-mobile-card__status-stack {
        align-items: flex-start;
        flex-direction: row;
        flex-wrap: wrap;
        max-width: none;
        width: 100%;
    }

    .objects-mobile-card__status,
    .objects-mobile-card__last-report {
        text-align: left;
    }

    .objects-mobile-card__signals,
    .objects-mobile-card__address,
    .objects-mobile-card__body,
    .objects-mobile-card__footer {
        padding-left: 14px;
    }

    .objects-mobile-card__body {
        grid-template-columns: repeat(2, minmax(0, 1fr));
    }

    .objects-mobile-card__footer {
        align-items: stretch;
        flex-direction: column;
    }

    .objects-mobile-card__footer .btn {
        width: 100%;
    }
}

@media (max-width: 420px) {
    .objects-mobile-card__body {
        grid-template-columns: 1fr;
    }
}
</style>
